import { useState, useEffect, Children, cloneElement, useRef } from 'react';
import classNames from 'classnames/bind';
import PropTypes from 'prop-types';

import { DSIcon } from '../../../base';

import styles from './Dropdown.module.scss';

const cx = classNames.bind(styles);

const Dropdown = ({
  className = '',
  title,
  arrowIcon = false,
  children,
  position = 'left',
  color = 'dark',
  itemsColor = null,
  textTransform = 'uppercase',
  dataTest = '',
  disableHover = false,
  roundedBorders = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const handleClickOutside = event => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsOpen(false);
    }
  };

  const listenOutsideClick = () => {
    window.addEventListener('click', handleClickOutside, false);
  };

  const unlistenOutsideClick = () => {
    window.removeEventListener('click', handleClickOutside, false);
  };

  const handleToggleDropdown = () => {
    if (!isOpen) {
      listenOutsideClick();
    } else {
      unlistenOutsideClick();
    }
    setIsOpen(!isOpen);
  };

  const handleValidClick = e => {
    if (e.target === e.currentTarget) {
      e.stopPropagation();
    }
  };

  useEffect(() => {
    return () => {
      unlistenOutsideClick();
    };
  }, []);

  const btnClassName = cx(
    'dropdown',
    {
      active: isOpen,
      left: position === 'left'
    },
    className,
    styles[color]
  );

  const btnActionClassName = cx(
    'action',
    styles[textTransform],
    styles[className]
  );

  const contentClasses = cx(styles.content, {
    [styles['content--rounded']]: roundedBorders
  });

  return (
    <div
      ref={dropdownRef}
      className={btnClassName}
      onMouseEnter={disableHover ? undefined : handleToggleDropdown}
      onMouseLeave={disableHover ? undefined : handleToggleDropdown}
      onClick={handleToggleDropdown}
      role="button"
      tabIndex="-1"
      data-test={dataTest}
    >
      <div className={btnActionClassName}>
        {typeof title === 'string' ? <span>{title}</span> : title}
        {arrowIcon && (
          <div className={styles['icon-container']}>
            <DSIcon name="arrow_full_bottom" size="s" />
          </div>
        )}
      </div>
      {isOpen && (
        <div
          role="button"
          tabIndex="-1"
          className={contentClasses}
          onClick={handleValidClick}
        >
          {Children.map(children, child =>
            cloneElement(child, { color: itemsColor || color })
          )}
        </div>
      )}
    </div>
  );
};

Dropdown.propTypes = {
  className: PropTypes.string,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  arrowIcon: PropTypes.bool,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ]).isRequired,
  position: PropTypes.oneOf(['left', 'right']),
  color: PropTypes.oneOf(['dark', 'light']),
  itemsColor: PropTypes.oneOf(['dark', 'light', 'light-redesign']),
  textTransform: PropTypes.oneOf(['uppercase', 'capitalize']),
  dataTest: PropTypes.string,
  disableHover: PropTypes.bool,
  roundedBorders: PropTypes.bool
};

export default Dropdown;
